import * as React from "react";
import clsx from "clsx";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Bug, CheckCircle2, CircleDashed, Loader } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import type { RoadmapItem, RoadmapStatus } from "../../lib/api/roadmap";
import { RoadmapCard } from "./RoadmapCard";
import * as s from "./RoadmapPage.css";

const SECTION_ICON: Record<RoadmapStatus, React.ReactNode> = {
  done: <CheckCircle2 size={16} color="currentColor" />,
  in_progress: <Loader size={16} color="currentColor" />,
  planned: <CircleDashed size={16} color="currentColor" />,
  known_bug: <Bug size={16} color="currentColor" />,
};

export function sectionDroppableId(status: RoadmapStatus): string {
  return `section:${status}`;
}

interface RoadmapSectionProps {
  status: RoadmapStatus;
  iconClassName: string;
  items: RoadmapItem[];
  onEdit: (item: RoadmapItem) => void;
  onDelete: (item: RoadmapItem) => void;
}

export function RoadmapSection({ status, iconClassName, items, onEdit, onDelete }: RoadmapSectionProps) {
  const { t } = useI18n();
  const { setNodeRef, isOver } = useDroppable({ id: sectionDroppableId(status) });

  return (
    <section ref={setNodeRef} className={clsx(s.section, isOver && s.sectionOver)}>
      <header className={s.sectionHeader}>
        <span className={clsx(s.sectionIcon, iconClassName)}>{SECTION_ICON[status]}</span>
        <h2 className={s.sectionTitle}>{t(`pro.system.roadmap.status.${status}`)}</h2>
        <span className={s.sectionCount}>{items.length}</span>
      </header>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={s.sectionList}>
          {items.length === 0 ? (
            <div className={s.sectionEmpty}>{t("pro.system.roadmap.section.empty")}</div>
          ) : (
            items.map((item) => (
              <RoadmapCard key={item.id} item={item} onEdit={onEdit} onDelete={onDelete} />
            ))
          )}
        </div>
      </SortableContext>
    </section>
  );
}
